import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Users, Mail, Sparkles, Send, ArrowLeft, CheckCircle2 } from "lucide-react";

import { trackUserLogin, trackClassJoined } from "@/utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Storage key for "remember me" functionality
const STUDENT_TOKEN_KEY = "lemonade_student_token";
const STUDENT_USER_KEY = "lemonade_student_user";

function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Tab state: "student" or "educator"
  const [activeTab, setActiveTab] = useState("student");
  
  // Educator login mode: "password" or "magic-link"
  const [educatorMode, setEducatorMode] = useState("password");
  
  // Email/Password login form
  const [emailFormData, setEmailFormData] = useState({
    email: "",
    password: ""
  });
  
  // Magic link form
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  
  // Class code login form
  const [classCodeFormData, setClassCodeFormData] = useState({
    classCode: "",
    displayName: "",
    rememberMe: true
  });
  
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Prefill class code from URL (?code=ABC123&path=middle_school)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const path = params.get("path");
    if (code) {
      setClassCodeFormData((prev) => ({ ...prev, classCode: code.toUpperCase() }));
      setActiveTab("student");
    }
    if (path === "middle_school") {
      // Save preference so post-login lands on Middle School dashboard
      localStorage.setItem("preferred_learning_path", "middle_school");
    }
  }, []);

  // Note: Auto-login is handled by App.js via the /dashboard redirect
  // We don't auto-login here because user might want to switch accounts

  const handleEmailChange = (e) => {
    setEmailFormData({ ...emailFormData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleClassCodeChange = (e) => {
    const { name, value, type, checked } = e.target;
    setClassCodeFormData({ 
      ...classCodeFormData, 
      [name]: type === "checkbox" ? checked : value 
    });
    setError("");
  };

  // Email/Password login (for educators)
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/login`, emailFormData);
      trackUserLogin({ role: response.data.user?.role, method: "email" });
      onLogin(response.data.user, response.data.access_token);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Magic Link request
  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMagicLinkSent(false);

    try {
      await axios.post(`${API}/auth/magic-link/request`, {
        email: magicLinkEmail
      });
      
      setMagicLinkSent(true);
      setSuccessMessage("Check your email for a login link!");
      
    } catch (err) {
      if (err.response?.status === 429) {
        setError("Too many requests. Please wait a few minutes and try again.");
      } else {
        // Still show success to prevent email enumeration
        setMagicLinkSent(true);
        setSuccessMessage("If an account exists for that email, you'll receive a login link.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Class Code login (for students)
  const handleClassCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await axios.post(`${API}/auth/student-login`, {
        class_code: classCodeFormData.classCode,
        display_name: classCodeFormData.displayName,
        remember_me: classCodeFormData.rememberMe
      });
      
      const { access_token, user, class_info } = response.data;
      
      // Save to localStorage if "remember me" is checked
      if (classCodeFormData.rememberMe) {
        localStorage.setItem(STUDENT_TOKEN_KEY, access_token);
        localStorage.setItem(STUDENT_USER_KEY, JSON.stringify(user));
      }
      
      // Show success briefly
      setSuccessMessage(`Welcome to ${class_info?.name || 'class'}!`);
      
      trackUserLogin({ role: "student", method: "class_code", className: class_info?.name });
      trackClassJoined({ role: "student", className: class_info?.name, classCode: classCodeFormData.classCode });
      
      setTimeout(() => {
        onLogin(user, access_token);
        navigate("/dashboard");
      }, 500);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Login failed. Please check your class code.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-10">
        <div className="bg-yellow-500 rounded-lg p-1">
          <LanguageSwitcher />
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-screen">
        <div className="auth-card w-full max-w-md p-6 sm:p-8">
          {/* Logo */}
          <div className="text-center mb-6">
            <img 
              src="/assets/lsbp-logo.png" 
              alt="The Lemonade Stand"
              className="mx-auto mb-4"
              style={{ maxWidth: "120px", height: "auto" }}
            />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">{t('login.welcomeBack')}</h1>
            <p className="text-gray-600 text-sm">{t('login.continueJourney')}</p>
          </div>

          {/* Tab Switcher */}
          <div className="flex mb-6 bg-gray-100 rounded-xl p-1" data-testid="login-tabs">
            <button
              onClick={() => { setActiveTab("student"); setError(""); setMagicLinkSent(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab === "student"
                  ? "bg-yellow-400 text-gray-900 shadow-md"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              data-testid="student-tab"
            >
              <Users className="w-4 h-4" />
              <span>Class Code</span>
            </button>
            <button
              onClick={() => { setActiveTab("educator"); setError(""); setMagicLinkSent(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab === "educator"
                  ? "bg-yellow-400 text-gray-900 shadow-md"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              data-testid="educator-tab"
            >
              <Mail className="w-4 h-4" />
              <span>Email</span>
            </button>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2" data-testid="success-message">
              <Sparkles className="w-5 h-5" />
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4" data-testid="error-message">
              {error}
            </div>
          )}

          {/* Student Login Form (Class Code) */}
          {activeTab === "student" && (
            <form onSubmit={handleClassCodeSubmit} data-testid="student-login-form">
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Class Code
                </label>
                <input
                  type="text"
                  name="classCode"
                  value={classCodeFormData.classCode}
                  onChange={handleClassCodeChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 uppercase text-center text-lg font-mono tracking-wider"
                  placeholder="ABC123"
                  required
                  autoComplete="off"
                  maxLength={10}
                  data-testid="class-code-input"
                />
                <p className="text-xs text-gray-500 mt-1">Ask your teacher for the class code</p>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Your First Name
                </label>
                <input
                  type="text"
                  name="displayName"
                  value={classCodeFormData.displayName}
                  onChange={handleClassCodeChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="Sarah"
                  required
                  autoComplete="given-name"
                  maxLength={50}
                  data-testid="display-name-input"
                />
              </div>

              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={classCodeFormData.rememberMe}
                    onChange={handleClassCodeChange}
                    className="w-4 h-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-400"
                    data-testid="remember-me-checkbox"
                  />
                  <span className="text-gray-700 text-sm">Remember me on this device</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="lemon-button w-full py-3 rounded-lg text-white font-bold text-lg disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="student-login-submit-btn"
              >
                {loading ? (
                  <>
                    <span className="animate-spin">🍋</span>
                    Joining...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Join Class
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">have your own account?</span>
                </div>
              </div>

              {/* Switch to Email Login */}
              <button
                type="button"
                onClick={() => setActiveTab("educator")}
                className="w-full py-2 text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center justify-center gap-2"
                data-testid="switch-to-email-btn"
              >
                <Mail className="w-4 h-4" />
                Sign in with email instead
              </button>
            </form>
          )}

          {/* Email Login Form (Email/Password or Magic Link) - For students AND educators */}
          {activeTab === "educator" && (
            <>
              {/* Magic Link Sent Success */}
              {magicLinkSent ? (
                <div className="text-center" data-testid="magic-link-sent">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800 mb-2">Check your email!</h2>
                  <p className="text-gray-600 mb-6">
                    If an account exists for <strong>{magicLinkEmail}</strong>, you'll receive a login link.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    The link expires in 30 minutes. Check your spam folder if you don't see it.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setMagicLinkSent(false); setMagicLinkEmail(""); }}
                    className="text-yellow-600 hover:text-yellow-700 font-medium flex items-center justify-center gap-2 mx-auto"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Try a different email
                  </button>
                </div>
              ) : (
                <>
                  {/* Password Login Form */}
                  {educatorMode === "password" && (
                    <form onSubmit={handleEmailSubmit} data-testid="educator-login-form">
                      <div className="mb-4">
                        <label className="block text-gray-700 font-semibold mb-2">{t('auth.email')}</label>
                        <input
                          type="email"
                          name="email"
                          value={emailFormData.email}
                          onChange={handleEmailChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          placeholder="your@email.com"
                          required
                          data-testid="email-input"
                        />
                      </div>

                      <div className="mb-6">
                        <label className="block text-gray-700 font-semibold mb-2">{t('auth.password')}</label>
                        <input
                          type="password"
                          name="password"
                          value={emailFormData.password}
                          onChange={handleEmailChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          placeholder="••••••••"
                          required
                          data-testid="password-input"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="lemon-button w-full py-3 rounded-lg text-white font-bold text-lg disabled:opacity-50"
                        data-testid="educator-login-submit-btn"
                      >
                        {loading ? t('common.loading') : t('auth.signIn')}
                      </button>

                      {/* Forgot Password */}
                      <div className="text-center mt-4">
                        <Link to="/forgot-password" className="text-yellow-600 hover:text-yellow-700 text-sm font-semibold" data-testid="forgot-password-link">
                          {t('auth.forgotPassword')}
                        </Link>
                      </div>

                      {/* Divider */}
                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">or</span>
                        </div>
                      </div>

                      {/* Magic Link Option */}
                      <button
                        type="button"
                        onClick={() => setEducatorMode("magic-link")}
                        className="w-full py-3 border-2 border-yellow-400 text-yellow-700 hover:bg-yellow-50 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                        data-testid="switch-to-magic-link-btn"
                      >
                        <Send className="w-4 h-4" />
                        Email me a login link
                      </button>

                      <p className="text-xs text-center text-gray-500 mt-2">
                        Best for school networks that block passwords
                      </p>

                      {/* Register Link */}
                      <div className="text-center mt-4">
                        <p className="text-gray-600 text-sm">
                          {t('auth.dontHaveAccount')}{" "}
                          <Link to="/register" className="text-yellow-600 hover:text-yellow-700 font-semibold" data-testid="register-link">
                            {t('auth.signUp')}
                          </Link>
                        </p>
                      </div>
                    </form>
                  )}

                  {/* Magic Link Form */}
                  {educatorMode === "magic-link" && (
                    <form onSubmit={handleMagicLinkSubmit} data-testid="magic-link-form">
                      <div className="text-center mb-6">
                        <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-3">
                          <Send className="w-6 h-6 text-yellow-600" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-800">Email Login Link</h2>
                        <p className="text-sm text-gray-600">We'll send you a secure link to sign in</p>
                      </div>

                      <div className="mb-6">
                        <label className="block text-gray-700 font-semibold mb-2">Email Address</label>
                        <input
                          type="email"
                          value={magicLinkEmail}
                          onChange={(e) => { setMagicLinkEmail(e.target.value); setError(""); }}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          placeholder="your@email.com"
                          required
                          data-testid="magic-link-email-input"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="lemon-button w-full py-3 rounded-lg text-white font-bold text-lg disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="magic-link-submit-btn"
                      >
                        {loading ? (
                          <>
                            <span className="animate-spin">🍋</span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Send Login Link
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => setEducatorMode("password")}
                        className="w-full mt-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center justify-center gap-2"
                        data-testid="back-to-password-btn"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back to password login
                      </button>
                    </form>
                  )}
                </>
              )}

              {/* Switch to Class Code Login - always visible in email tab */}
              {!magicLinkSent && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">joining a class?</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveTab("student")}
                    className="w-full py-2 text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center justify-center gap-2"
                    data-testid="switch-to-student-btn"
                  >
                    <Users className="w-4 h-4" />
                    Join with class code instead
                  </button>
                </>
              )}
            </>
          )}

          {/* Back to Home */}
          <div className="text-center mt-6">
            <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm" data-testid="back-home-link">
              ← {t('login.backToHome')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
